import { useQuery } from '@tanstack/react-query';
import { CREDITS, type AboutResponse, type Credit } from '@couch/shared';
import { Link } from 'react-router';
import { api, errorMessage } from '../api/client';

const GROUPS: readonly { id: Credit['group']; title: string }[] = [
  { id: 'machine', title: 'On your machine' },
  { id: 'server', title: 'The Couch server' },
  { id: 'web', title: 'The web app' },
];

function Versions({ about }: { about: AboutResponse }) {
  const rows: [string, string][] = [
    ['Couch', about.couch.build ? `${about.couch.version} (build ${about.couch.build})` : about.couch.version],
    ['Couch agent (served to machines)', about.agent],
    ['ComfyUI (installed and updated to)', about.comfyui],
    ['Knowledge base', `${about.knowledge.topics} topics, checked up to ${about.knowledge.lastVerified}`],
  ];
  return <dl className="about-versions">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

function Credits() {
  return (
    <section className="panel" aria-labelledby="about-credits">
      <h2 id="about-credits">Licenses and credits</h2>
      <p className="muted">
        Couch is built on the work of these projects. ComfyUI runs unmodified as its own program on your machine. Every AI model
        has its own license — see the <Link to="/knowledge/models">model licenses in Knowledge</Link>.
      </p>
      {GROUPS.map((g) => (
        <div key={g.id} className="about-credit-group">
          <h3>{g.title}</h3>
          <ul className="about-credits">
            {CREDITS.filter((c) => c.group === g.id).map((c) => (
              <li key={c.name}>
                <a href={c.url} target="_blank" rel="noopener noreferrer">{c.name}</a> <span className="muted">— {c.role} · {c.license}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** The About tab (doc 45): what Couch is, how it works, versions, licenses and credits. */
export function AboutPage() {
  const about = useQuery({ queryKey: ['about'], queryFn: api.about, staleTime: 10 * 60_000 });
  return (
    <section aria-labelledby="about-title" className="about">
      <h1 id="about-title">About Couch</h1>
      <p>
        Couch runs ComfyUI on your own Macs and PCs and lets you — or Claude — build, run and keep image workflows from any
        browser. Friends you invite get their own account and their own machines; nobody can use someone else&apos;s computer.
      </p>

      <section className="panel" aria-labelledby="about-how">
        <h2 id="about-how">How it works</h2>
        <ul>
          <li>This page, your account and your workflows live on the Couch server. The computing happens on your own machines.</li>
          <li>The small Couch agent on each machine connects out to Couch — no open port at home — and drives ComfyUI there.</li>
          <li>Models download straight from Hugging Face or Civitai to your machine, never through the server.</li>
          <li>
            The server keeps: accounts and sign-ins, your machines&apos; names and hardware, workflows with their versions, run
            records, small gallery previews, Claude-tab conversations with the pictures you gave Claude (made smaller), your
            provider keys (encrypted), API tokens and app connections (only as hashes), invites and shares.
          </li>
          <li>Your original images stay on your machine, and so do models and ComfyUI itself; the server generates nothing.</li>
        </ul>
      </section>

      <section className="panel" aria-labelledby="about-versions">
        <h2 id="about-versions">Versions</h2>
        {about.isPending && <p role="status">Loading…</p>}
        {about.isError && <p role="alert" className="form-error">{errorMessage(about.error)}</p>}
        {about.isSuccess && <Versions about={about.data} />}
      </section>

      <Credits />
    </section>
  );
}
